// @ts-strict-ignore
import { createSelector } from 'reselect';
import type { State } from '~/store';
import {
  selectHasInputAttributeNodeFolder,
  selectNodeLineageForAnchorNode,
  selectWorkflowGraph,
} from './graph';
import type { FolderNode } from 'flow/service/graph';
import {
  computeNodeState, filterNodesBySearch, getNodeIds, getNodeLabel, groupNodesById, isFolderNode,
} from 'flow/service/graph';
import { selectNodeFolderState, selectSearchMatchIndex } from './ui';
import { selectMergeDiff } from './snippets';


export const selectWorkflowNodeUIState = createSelector(
  [
    (state: State) => state.workflowEditor.ui.hoveredNodeId,
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    (state: State) => state.workflowEditor.ui.activeNodeEditorTab,
    (state: State) => state.workflowEditor.ui.highlightedNodeId,
    (state: State) => state.workflowEditor.ui.floatedAnchorNodeId,
    (state: State) => state.workflowEditor.ui.nodeFolders,
  ],
  (
    hoveredNodeId,
    focusedNodeId,
    activeNodeEditorTab,
    highlightedNodeId,
    floatedAnchorNodeId,
    nodeFolders,
  ) => ({
    focusedNodeId,
    activeNodeEditorTab,
    floatedAnchorNodeId,
    hoveredNodeId,
    highlightedNodeId,
    nodeFolders,
  }),
);

export const selectFolderNode = createSelector(
  [
    selectWorkflowGraph,
    selectHasInputAttributeNodeFolder,
  ],
  (graph, hasInputAttributeNodeFolder): FolderNode => {
    if (!hasInputAttributeNodeFolder) {
      return null;
    }
    return graph.nodes.find(isFolderNode);
  },
);

export const selectFolderNodeChildren = createSelector(
  [
    selectFolderNode,
  ],
  folderNode => (folderNode ? folderNode.data.children : null),
);

export const selectHasMatchedCollapsedNodes = createSelector(
  [
    selectFolderNode,
    (state: State) => state.workflowEditor.ui.search.query,
  ],
  (inputAttributeFolder, searchQuery) => {
    if (!inputAttributeFolder) return false;
    return (
      // nested nodes are only populated in data.children when collapsed
      filterNodesBySearch(inputAttributeFolder.data.children, searchQuery).length > 0
    );
  },
);

export const selectNodesMatchingSearch = createSelector(
  [
    selectWorkflowGraph,
    (state: State) => state.workflowEditor.ui.search.query,
  ],
  (graph, searchQuery) => filterNodesBySearch(graph.nodes, searchQuery),
);

export const selectNodeIdsMatchingSearch = createSelector(
  [
    selectNodesMatchingSearch,
  ],
  nodesMatchingSearch => getNodeIds(nodesMatchingSearch),
);

export const selectNodeAtSearchMatchIndex = createSelector(
  [
    selectSearchMatchIndex,
    selectNodesMatchingSearch,
  ],
  (searchMatchIndex, matchedNodes) => matchedNodes.at(searchMatchIndex),
);

export const selectNodesById = createSelector(
  [
    selectWorkflowGraph,
  ],
  graph => groupNodesById(graph.nodes),
);


export const selectNodeState = createSelector(
  [
    (state: State) => state.workflowEditor.ui.hoveredNodeId,
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    (state: State) => state.workflowEditor.ui.activeNodeEditorTab,
    (state: State) => state.workflowEditor.ui.highlightedNodeId,
    (state: State) => state.workflowEditor.ui.search,
    selectNodeLineageForAnchorNode,
    selectNodeIdsMatchingSearch,
    selectNodesById,
    selectMergeDiff,
    selectNodeFolderState,
    (state: State, nodeId: string) => nodeId,
  ],
  (
    hoveredNodeId,
    focusedNodeId,
    activeNodeEditorTab,
    highlightedNodeId,
    searchState,
    nodeLineage,
    nodeIdsMatchingSearch,
    nodesById,
    mergeDiff,
    nodeFolder,
    nodeId,
  ) => computeNodeState({
    nodePartial: {
      nodeId,
      nodeLabel: getNodeLabel(nodesById[nodeId]),
    },
    uiStatePartial: {
      activeNodeEditorTab,
      focusedNodeId,
      highlightedNodeId,
      hoveredNodeId,
      searchState,
      nodeFolder,
    },
    nodeLineage,
    nodeIdsMatchingSearch,
    mergeDiff,
  }),
);

